import React, { useEffect, useMemo, useRef, useState } from "react";
import PageWrapper from "@components/layouts/PageWrapper";
import { useLocation } from "react-router-dom";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import * as XLSX from "xlsx";
import { useGetSalesByProductCustomerQuery } from "@states/report/report.api";
import ReportFilter from "@components/filters/ReportFilter";
import CustomButton from "@components/buttons/CustomButton";
import { formatFilterDate } from "@utils/general/format-filter-date";
import { thousandSeperator } from "@utils/general/digit-separators";

const SalesByProductCustomerPage = () => {
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);

  const filterRef = useRef({
    from_date: undefined,
    to_date: undefined,
  });
  const location = useLocation();

  const defaultFilters = useMemo(
    () => ({
      from_date: formatFilterDate(lastMonth),
      to_date: formatFilterDate(today),
    }),
    [lastMonth, today]
  );
  const [pendingFilters, setPendingFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);

  const {
    data,
    isFetching,
    error: getAllError,
    refetch,
  } = useGetSalesByProductCustomerQuery(appliedFilters);

  const rows = Array.isArray(data?.data) ? data.data : [];

  const groupedData = rows.reduce((acc: any[], item: any) => {
    const existingCustomer = acc.find(
      (customer) => customer.customer_id === item.customer_id
    );

    if (existingCustomer) {
      existingCustomer.products.push(item);
    } else {
      acc.push({
        customer_id: item.customer_id,
        customer_first_name: item.customer_first_name,
        products: [item],
      });
    }

    return acc;
  }, []);

  const formatData = groupedData.reduce((acc: any[], customer: any) => {
    acc.push({
      isSection: true,
      customer_first_name: customer.customer_first_name,
    });

    customer.products.forEach((product: any) => {
      acc.push({
        ...product,
        isSection: false,
      });
    });

    return acc;
  }, []);

  const totals = formatData.reduce(
    (acc: any, row: any) => {
      if (!row.isSection) {
        acc.totalTripCashAmount += Number(row.trip_cash_amount || 0);
        acc.totalSaleAmount += Number(row.total_sale_amount || 0);
        acc.totalRegularCashAmount += Number(row.regular_cash_amount || 0);
        acc.totalTripCreditAmount += Number(row.trip_credit_amount || 0);
        acc.totalRegularCreditAmount += Number(row.regular_credit_amount || 0);
      }
      return acc;
    },
    {
      totalTripCashAmount: 0,
      totalSaleAmount: 0,
      totalRegularCashAmount: 0,
      totalTripCreditAmount: 0,
      totalRegularCreditAmount: 0,
    }
  );

  useErrorHandling(...castTypeArr([getAllError]));

  useEffect(() => {
    if (location.state?.reload) {
      refetch();
    }
  }, [location.state?.reload, refetch]);

  const handleExportClick = () => {
    if (!formatData.length) return;

    const excelData: any[] = [];

    excelData.push([
      "Product ID",
      "Product Name",
      "Product Category",
      "Unit",
      "Pack Type",
      "Regular Cash Amount",
      "Regular Credit Amount",
      "Trip Cash Amount",
      "Trip Credit Amount",
      "Total Amount",
    ]);

    formatData.forEach((row: any) => {
      if (row.isSection) {
        excelData.push([
          `Customer Name: ${row.customer_first_name}`,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ]);
      } else {
        excelData.push([
          row.product_id,
          row.product_name,
          row.product_category_name,
          row.unit_name,
          row.product_type_name,
          row.regular_cash_amount,
          row.regular_credit_amount,
          row.trip_cash_amount,
          row.trip_credit_amount,
          row.total_sale_amount,
        ]);
      }
    });

    excelData.push([
      "Totals:",
      "",
      "",
      "",
      "",
      totals.totalRegularCashAmount,
      totals.totalRegularCreditAmount,
      totals.totalTripCashAmount,
      totals.totalTripCreditAmount,
      totals.totalSaleAmount,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    const merges: XLSX.Range[] = [];

    formatData.forEach((row: any, index: number) => {
      if (row.isSection) {
        merges.push({
          s: { r: index + 1, c: 0 },
          e: { r: index + 1, c: 9 },
        });
      }
    });

    worksheet["!merges"] = merges;
    worksheet["!cols"] = [
      { wch: 15 },
      { wch: 25 },
      { wch: 20 },
      { wch: 10 },
      { wch: 10 },
      { wch: 18 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Sales By Product Customer Report"
    );
    XLSX.writeFile(workbook, "sales-by-product-customer-report.xlsx");
  };

  return (
    <>
      {isFetching && <LoadingSpinner />}
      <PageWrapper title="Sales By Product Customer Report">
        <div className="grid grid-cols-8 justify-evenly">
          <div className="col-span-6">
            <ReportFilter
              refetch={refetch}
              filterRef={filterRef}
              onFilterChange={(filters) =>
                setPendingFilters((prev) => ({ ...prev, ...filters }))
              }
              onApply={() => setAppliedFilters(pendingFilters)}
            />
          </div>
          <div className="flex items-center justify-end mt-2 col-span-2">
            <CustomButton
              title={
                <div className="flex gap-3">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.4126 17.2325V1H10.102C6.73567 1.60819 3.37848 2.17076 0 2.75462V18.1083C3.32374 18.6952 6.69006 19.2821 10.0351 19.8903H11.4035V17.2325H11.4126ZM6.46198 13.8632C6.04269 12.9666 5.67515 12.0467 5.36117 11.1081C5.05707 11.9991 4.63742 12.8384 4.2938 13.7111C3.81333 13.7111 3.33286 13.6837 2.8524 13.6655C3.41497 12.5616 3.9593 11.4486 4.54011 10.3539C4.04748 9.22268 3.50316 8.11579 2.99532 6.99368L4.44889 6.91158C4.77427 7.76912 5.1331 8.61754 5.40374 9.49637C5.70783 8.5628 6.12444 7.67181 6.49544 6.78386C6.99212 6.74939 7.48982 6.71898 7.98853 6.69263C7.40467 7.89278 6.81575 9.09598 6.22175 10.3022C6.82994 11.5186 7.43813 12.7502 8.04631 13.9787C7.51719 13.9361 6.99111 13.9027 6.46198 13.8632Z"
                      fill="white"
                    />
                    <path
                      d="M11.9658 3.10352V4.26819H14.2921V5.98632H11.9658V6.92901H14.2921V8.64714H11.9658V9.58983H14.2921V11.3049H11.9658V12.2476H14.2921V13.9505H11.9658V14.948H14.2921V16.6113H11.9658V17.776H20V3.10352H11.9658ZM18.2271 16.6235H14.8456V14.9601H18.2271V16.6235ZM18.2271 13.9627H14.8456V12.2446H18.2271V13.9627ZM18.2271 11.3019H14.8456V9.58679H18.2271V11.3019ZM18.2271 8.64106H14.8456V6.92597H18.2271V8.64106ZM18.2271 5.98024H14.8456V4.26819H18.2271V5.98024Z"
                      fill="white"
                    />
                  </svg>
                  Export Excel
                </div>
              }
              color="bg-[#1E6E42] h-3/6"
              handleClick={handleExportClick}
            />
          </div>
        </div>
        <table className="w-full border-collapse border-[1px] border-gray-200 rounded-xl">
          <thead>
            <tr>
              <th className="text-gray-400 text-sm pr-4 py-4">Product ID</th>
              <th className="text-gray-400 text-sm pr-4">Product Name</th>
              <th className="text-gray-400 text-sm pr-4">Product Category</th>
              <th className="text-gray-400 text-sm pr-4">Unit</th>
              <th className="text-gray-400 text-sm pr-4">Pack Type</th>
              <th className="text-gray-400 text-sm pr-4">
                Regular Cash Amount
              </th>
              <th className="text-gray-400 text-sm pr-4">
                Regular Credit Amount
              </th>
              <th className="text-gray-400 text-sm pr-4">Trip Cash Amount</th>
              <th className="text-gray-400 text-sm pr-4">Trip Credit Amount</th>
              <th className="text-gray-400 text-sm pr-4">Total Amount</th>
            </tr>
          </thead>
          <tbody>
            {formatData.map((row: any, index: number) => (
              <tr
                key={row.isSection ? `section-${index}` : `${row.customer_id}-${row.product_id}-${index}`}
                className={row.isSection ? "bg-blue-200 font-bold text-start" : ""}
              >
                {row.isSection ? (
                  <td colSpan={10} className="py-2 px-4 text-[#002C76]">
                    <div className="flex justify-between items-center">
                      <p>Customer Name : {row.customer_first_name}</p>
                    </div>
                  </td>
                ) : (
                  <>
                    <td className="border p-2 px-4">{row.product_id}</td>
                    <td className="border p-2 px-4">{row.product_name}</td>
                    <td className="border p-2 px-4">{row.product_category_name}</td>
                    <td className="border p-2 px-4">{row.unit_name}</td>
                    <td className="border p-2 px-4">{row.product_type_name}</td>
                    <td className="border p-2 px-4">{thousandSeperator(row.regular_cash_amount || 0)}</td>
                    <td className="border p-2 px-4">{thousandSeperator(row.regular_credit_amount || 0)}</td>
                    <td className="border p-2 px-4">{thousandSeperator(row.trip_cash_amount || 0)}</td>
                    <td className="border p-2 px-4">{thousandSeperator(row.trip_credit_amount || 0)}</td>
                    <td className="border p-2 px-4">{thousandSeperator(row.total_sale_amount || 0)}</td>
                  </>
                )}
              </tr>
            ))}
            <tr className="font-bold">
              <td colSpan={5} className="p-2 px-4 text-start">
                Totals:
              </td>
              <td className="p-2">{thousandSeperator(totals.totalRegularCashAmount)}</td>
              <td className="p-2">{thousandSeperator(totals.totalRegularCreditAmount)}</td>
              <td className="p-2">{thousandSeperator(totals.totalTripCashAmount)}</td>
              <td className="p-2">{thousandSeperator(totals.totalTripCreditAmount)}</td>
              <td className="p-2">{thousandSeperator(totals.totalSaleAmount)}</td>
            </tr>
          </tbody>
        </table>
      </PageWrapper>
    </>
  );
};

export default SalesByProductCustomerPage;
